import React, { useEffect, useState, forwardRef } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02"];

export const SalesReportPage = forwardRef(({ reportRange, rawData }, ref) => {
    const theme = useTheme();
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [orderSummary, setOrderSummary] = useState({});
    const [paymentSummary, setPaymentSummary] = useState({});
    const [cashDropTotal, setCashDropTotal] = useState(0);
    const [paidOutTotal, setPaidOutTotal] = useState(0);
    const [salesSummary, setSalesSummary] = useState({});

       

    useEffect(() => {
        // --- MODIFIED LOGIC ---
        // Check if rawData is a valid array
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            console.log("Did not find valid array data to process.");
            setChartData([]);
            setPieData([]);
            setOrderSummary({});
            setPaymentSummary({});
            setCashDropTotal(0);
            setPaidOutTotal(0);
            setSalesSummary({});
            return;
        }

        const hourlySales = {};
        const paymentMap = { Cash: 0, "Credit Card": 0, "Gift Card": 0, Online: 0, Other: 0 };
        let tipTotal = 0;
        let completed = 0;
        let voided = 0; // Initialize aggregator
        const orderTypeMap = { "In-store": 0, Pickup: 0, Kiosk: 0, Online: 0, Other: 0 };

        let aggregatedCashDrops = 0;
        let aggregatedPaidOuts = 0;
        let aggregatedTotalSales = 0;
        let aggregatedTotalTax = 0;

        // --- NEW OUTER LOOP ---
        // Loop through each DAY in the rawData array
        rawData.forEach(dayData => {

            // Aggregate voided orders from each day
            voided += dayData.cancelOrders?.length ?? 0;

            // Loop through completed orders for THIS day
            for (let order of dayData.completedOrders || []) {
                const hour = new Date(order.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                hourlySales[hour] = (hourlySales[hour] || 0) + (order.total ?? 0);
                tipTotal += order.Tip ?? 0;
                completed++;

                // Loop payments for this order
                for (let payment of order.payment || []) {
                    switch (payment.payType) {
                        case 0: paymentMap["Cash"] += (payment.amount ?? 0); break;
                        case 1: paymentMap["Gift Card"] += (payment.amount ?? 0); break;
                        case 2: paymentMap["Online"] += (payment.amount ?? 0); break;
                        case 3: paymentMap["Credit Card"] += (payment.amount ?? 0); break;
                        default: paymentMap["Other"] += (payment.amount ?? 0);
                    }
                }

                // Map order type
                const typeName =
                    order.type === 0 ? "In-store" :
                        order.type === 1 ? "Pickup" :
                            order.type === 2 ? "Kiosk" :
                                order.type === 3 ? "Online" : "Other";
                orderTypeMap[typeName] = (orderTypeMap[typeName] || 0) + 1;
            }

            // Aggregate cash drops and paid outs from THIS day
            aggregatedCashDrops += (dayData.cashDrops || []).reduce((acc, drop) => acc + (drop.amount ?? 0), 0);
            aggregatedPaidOuts += (dayData.paidOuts || []).reduce((acc, payout) => acc + (payout.amount ?? 0), 0);

            // Aggregate totals from THIS day
            aggregatedTotalSales += dayData.totalSales ?? 0;
            aggregatedTotalTax += dayData.totalTax ?? 0;
        });
        // --- END OF NEW OUTER LOOP ---

        // Get opening cash from the *first* day
        const openingCash = rawData[0]?.openingCash ?? 0;
        // Get till cash from the *last* day
        const tillCash = rawData[rawData.length - 1]?.tillCash ?? 0;

        setChartData(Object.entries(hourlySales).map(([date, total]) => ({ date, total })));
        setPieData(Object.entries(paymentMap).map(([name, value]) => ({ name, value })));
        setOrderSummary({ ...orderTypeMap, Completed: completed, Void: voided });
        setPaymentSummary(paymentMap);
        setCashDropTotal(aggregatedCashDrops); // Use aggregated value
        setPaidOutTotal(aggregatedPaidOuts); // Use aggregated value

        const expectedCash = openingCash + paymentMap["Cash"] - aggregatedCashDrops - aggregatedPaidOuts;
        const difference = tillCash - expectedCash;

        setSalesSummary({
            subTotal: aggregatedTotalSales, // Use aggregated value
            tax: aggregatedTotalTax, // Use aggregated value
            total: aggregatedTotalSales, // Use aggregated value
            tips: tipTotal,
            openingCash: openingCash,
            cashCount: tillCash,
            overShort: difference.toFixed(2)
        });

    }, [reportRange, rawData]);


    return (
        <Container sx={{ mt: 4, mb: 4 }} ref={ref}>
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f5faff" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Overview
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <LineChart width={400} height={250} data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="total" stroke={theme.palette.primary.main} />
                        </LineChart>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PieChart width={300} height={250}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff8e1" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order Type</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(orderSummary).map(([type, count]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        <TableCell align="right">{count}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e1f5fe" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Payment Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Payment Type</TableCell>
                                    <TableCell align="right">Amount ($)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(paymentSummary).map(([type, amount]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        {/* Use optional chaining for safe number formatting */}
                                        <TableCell align="right">${amount?.toFixed(2) ?? '0.00'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <br />

                        <Typography variant="subtitle2">Total Cash Drops : ${cashDropTotal?.toFixed(2) ?? '0.00'}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Total Paid Outs : ${paidOutTotal?.toFixed(2) ?? '0.00'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: "#f3e5f5" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Summary
                </Typography>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>Sub Total</TableCell>
                            <TableCell align="right">${salesSummary.subTotal?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tax</TableCell>
                            <TableCell align="right">${salesSummary.tax?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Total Sale</TableCell>
                            <TableCell align="right">${salesSummary.total?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tips</TableCell>
                            <TableCell align="right">${salesSummary.tips?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Opening Cash</TableCell>
                            <TableCell align="right">${salesSummary.openingCash?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Cash Count</TableCell>
                            <TableCell align="right">${salesSummary.cashCount?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Over/Short</TableCell>
                            <TableCell align="right" sx={{ color: "error.main" }}>
                                ${salesSummary.overShort ?? '0.00'}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
        </Container>
    );
});
